import { type ExtensionApi } from '../../../core/extension';
import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'dynamicPie',
  name_en: 'Dynamic Pie',
  // name_zh: '动态饼图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    data: (amount: number) => amount > 0 && amount <= 10,
    x: (amount: number) => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    date: {
      itemType: [DEFINE.DATE],
      category: DEFINE.SINGLE,
      optional: true
    }
  }
};

/**
 * ECharts 的配置项
 * @see https://echarts.apache.org/zh/option.html#series-pie
 */
type EChartsPieSeries = {
  roseType?: unknown;
  radius?: unknown;
};

export interface Spec extends StandardChartLayerSpec, EChartsPieSeries {
  encoding: {
    x: string;
    y: string;
    // Add the 'date' property manually
    date?: string;
  };
}

interface DataPoint {
  x: string;
  y: number;
  date: string;
}

/**
 * 解析处理数据
 * 对数据进行排序、过滤、转换、生成日期数据等
 * @param api
 * @param spec
 * @returns
 */
function processData(api: ExtensionApi, spec: Spec) {
  const { data: dataArray, util } = api;
  const {
    dataIndex = 0,
    encoding: { x, y: tempY, date = 'date' }
  } = spec;
  // 兜底数组情况，饼图y字段默认不支持数组
  const y = (Array.isArray(tempY) ? tempY[0] : tempY) as string;
  const data = dataArray[dataIndex];

  const transformData = util.dataTransform.transform(data.values.slice(), [
    { type: 'sort', field: y, order: 'descending' }
  ]);

  const dynamicPieData = transformData.map(item => ({
    __origin: transformData,
    name: item[x],
    value: +item[y],
    date: item[date]
  }));
  const { dates } = util.dataEncoding.encoding(dynamicPieData, {
    dates: {
      field: 'date',
      transforms: [{ type: 'deDuplication' }, { type: 'sort', order: 'ascending' }]
    }
  });

  const dataArr: DataPoint[][] = dates.map(date1 => {
    return util.dataTransform.transform(transformData, [
      { type: 'filter', cb: item => item[date] === date1 }
    ]);
  });

  return { dataArr, dates };
}

/**
 *  配置Series
 * @param api
 * @param spec
 * @returns
 */
function configureSeriesOption(api: ExtensionApi, spec: Spec) {
  const { layout, util } = api;
  const { roseType = false, radius } = spec;
  const showLabel = ![util.const.LAYOUT.SMALL, util.const.LAYOUT.SMALL_1_2].includes(layout);
  let seriesOption = {
    radius: showLabel ? '75%' : '90%',
    label: {
      show: showLabel
    }
  } as unknown;
  if (typeof roseType !== 'undefined') {
    seriesOption = {
      roseType,
      radius: showLabel ? '75%' : '90%',
      label: {
        show: showLabel
      }
    };
  }
  if (typeof radius !== 'undefined') {
    seriesOption = {
      radius,
      avoidLabelOverlap: false,
      label: {
        show: false,
        position: 'center'
      },
      labelLine: {
        show: false
      },
      emphasis: {
        label: {
          show: true,
          fontSize: layout.includes('lg') ? 24 : 12
        }
      }
    };
  }

  return seriesOption;
}

/**
 * 生成每个周期所需的Option配置
 * @param api
 * @param spec
 * @param dataArr
 * @param seriesOption
 * @returns
 */
function preparePeriodOption(api: ExtensionApi, spec: Spec, dataArr: any[], seriesOption: unknown) {
  const { util, hook } = api;
  const {
    encoding: { x, y }
  } = spec;
  // 预生成每个周期的配置项
  return dataArr.map(dataItem => {
    const dataMetaInfo = hook.parseDataMetaInfo(dataItem);
    // 格式化数据
    const formatData: { name: string; value: number }[] = dataItem.map((item: DataPoint) => {
      return {
        name: item[x],
        value: +item[y]
      };
    });

    // 按照 name 排序，以解决播放过程中相对顺序变化导致扇区位置突变的问题
    formatData.sort((a, b) => a.name.localeCompare(b.name));

    // 生成每一周期的配置项
    return {
      tooltip: {
        formatter: params => {
          const series = Array.isArray(params) ? params : [params];
          const items = [] as Parameters<typeof hook.buildTooltip>[0]['items'];

          series.forEach(item => {
            const result = hook.formatDataNumberValue(item.value, {
              type: 'table',
              unit: dataMetaInfo[item.seriesName]?.unit
            });

            items.push({
              type: util.const.SERIES_2_LEGEND_TYPE_MAP[item.seriesType],
              color: item.color,
              name: item.name,
              value: result.value + result.unit
            });
          });

          return hook.buildTooltip({
            config: hook.buildStandardChartPresetOption(api).tooltip,
            title: series?.[0]?.seriesName,
            items
          });
        }
      },
      xAxis: [],
      yAxis: [],
      series: [
        {
          ...(seriesOption as Record<string, unknown>),
          type: 'pie',
          name: y,
          data: formatData
        }
      ]
    };
  });
}

export const dynamicPie = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  // TODO:这个的作用是什么？
  isIndependentLayer: true,
  // 必须加上这个，否则生成timeline实例会失败
  withTimeline: true,
  visInfo,
  render(api, spec, $dom) {
    // * -------------------------- data
    const { dataArr, dates } = processData(api, spec);

    // * -------------------------- chart
    const seriesOption = configureSeriesOption(api, spec);

    const options = preparePeriodOption(api, spec, dataArr, seriesOption);
    // * -------------------------- timeline
    return renderApi => {
      const renderParams = {
        option: renderApi.mergeChartOptionProxy(options[options.length - 1])
      };

      const timelineIns = renderApi.renderTimelineProxy(dates as string[]);
      const legendIns = renderApi.renderLegendProxy(renderParams.option?.legend?.data);
      // TODO:显示的是黑色风格？
      const chartIns = renderApi.renderChartProxy(renderParams, { theme: 'pc-app-light' });

      timelineIns.on('change', e => {
        const nextOption = renderApi.mergeChartOptionProxy(options[e.index]);
        renderApi.optionCallbackProxy(nextOption);

        // TODO:未传递legend选中状态（echarts的legend选中状态和组件legend的选中状态的同步比较麻烦）
        legendIns?.update(nextOption.legend.data);
        renderParams.option = nextOption;
        api.hook.beforeRenderStandardChart(api, renderParams);

        chartIns.play(renderParams);

        renderApi.cacheRenderOptionProxy(renderParams.option);
      });
    };
  }
});
